import { useEffect, useRef, useState } from "react";
import { Aperture, Leaf, FileText, Shield, Camera, Zap, ChevronLeft, ChevronRight, Pause, Play, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { NsButton } from "@/components/new-site/NewSiteChrome";
import NewSiteProcessSection, { operatingModelSteps } from "@/components/new-site/NewSiteProcessSection";
import { aiExamplesByTopic } from "./aiExamples";
import "./mobile-home.css";
import heroVideo from "@/assets/new-site/hero-video-main.mp4.asset.json";
import heroMobileVideo from "@/assets/new-site/hero-mobile.mp4.asset.json";
import heroMobilePoster from "@/assets/new-site/hero-mobile-poster.jpg.asset.json";
import heroDesktopPoster from "@/assets/new-site/hero-desktop-poster.jpg.asset.json";
import fostonBeckAsset from "@/assets/new-site/foston-beck-national-highways.jpg.asset.json";
import fostonBeckMobileAsset from "@/assets/new-site/foston-beck-mobile.jpg.asset.json";
import fieldProvenVideo from "@/assets/new-site/field-proven-video.mp4.asset.json";
import periodicImagesDashboard from "@/assets/new-site/periodic-images-dashboard.jpg.asset.json";
import instantImagesDeviceOverview from "@/assets/new-site/instant-images-phone-closeup.jpg.asset.json";
import privacyGuaranteedStreet from "@/assets/new-site/privacy-guaranteed-street.jpg.asset.json";
import alertsNotificationsRoadworks from "@/assets/new-site/alerts-notifications-photo.jpg.asset.json";
import koriLogo from "@/assets/kori-logo.png";
import crowdguardLogo from "@/assets/crowdguard-logo.png";
import nationalGrid from "@/assets/logo-national-grid.png";
import tlNetworkRail from "@/assets/new-site/trusted/network-rail.svg";
import tlKori from "@/assets/new-site/trusted/kori.svg";
import tlForest from "@/assets/new-site/trusted/nottingham-forest.svg";
import tlPrevisico from "@/assets/new-site/trusted/previsico.svg";
import tlHighways from "@/assets/new-site/trusted/national-highways.svg";
import tlLeeds from "@/assets/new-site/trusted/leeds-united.svg";
import tlKaarbon from "@/assets/new-site/trusted/kaarbontech.svg";
import tlCrowdguard from "@/assets/new-site/trusted/crowdguard.png.asset.json";
import tlGrid from "@/assets/new-site/trusted/national-grid.svg";
import tlLeicester from "@/assets/new-site/trusted/leicester-city.svg";
import tlKent from "@/assets/new-site/trusted/kent-county-council.png.asset.json";
import tlCore from "@/assets/new-site/trusted/core-highways.png.asset.json";
import tlAtm from "@/assets/new-site/trusted/atm-traffic-management.png.asset.json";
import tlDorset from "@/assets/new-site/trusted/dorset-council.png.asset.json";
import tlWessex from "@/assets/new-site/trusted/wessex-water.png.asset.json";
import nationalHighwaysMark from "@/assets/new-site/national-highways-mark.png.asset.json";
import kentCrest from "@/assets/new-site/kent-county-council-crest.png.asset.json";
import crowdguardBadge from "@/assets/new-site/crowdguard-badge.png.asset.json";
import previsicoBadge from "@/assets/new-site/previsico-badge.png.asset.json";


// Figma "desktop-trusted-by": exact logo sizes, in visual order (row 1 then row 2)
const trustedLogos = [
  { name: "Network Rail", src: tlNetworkRail, w: 112.6, h: 42.5 },
  { name: "Previsico", src: tlPrevisico, w: 150.8, h: 36.4 },
  { name: "National Grid", src: tlGrid, w: 149.4, h: 27.1 },
  { name: "Kori", src: tlKori, w: 126.7, h: 31.4 },
  { name: "National Highways", src: tlHighways, w: 112.3, h: 32.7 },
  { name: "Nottingham Forest", src: tlForest, w: 49.8, h: 107.7 },
  { name: "Leeds United", src: tlLeeds, w: 63.4, h: 78.1 },
  { name: "KaarbonTech", src: tlKaarbon, w: 173.7, h: 38.4 },
  { name: "CrowdGuard", src: tlCrowdguard.url, w: 86.3, h: 72 },
  { name: "Leicester City", src: tlLeicester, w: 69.3, h: 71 },
  { name: "Kent County Council", src: tlKent.url, w: 95, h: 61.9 },
  { name: "Core Highways", src: tlCore.url, w: 79.8, h: 34 },
  { name: "ATM Highway Traffic Management", src: tlAtm.url, w: 75.2, h: 48 },
  { name: "Dorset Council", src: tlDorset.url, w: 108, h: 48 },
  { name: "Wessex Water", src: tlWessex.url, w: 170, h: 42 },

];

// Testimonial carousel (Figma "desktop-testimonials"). Add an entry here to show another quote.
const testimonials = [
  {
    company: "Previsico",
    quote:
      "Snapshot Live supports our flood monitoring work with autonomous cameras that provide reliable visibility where we need it most.",
    name: "Dr Steven Hayward",
    role: "Previsico",
    logo: previsicoBadge.url,
  },
  {
    company: "National Highways",
    quote:
      "The sensors and cameras have allowed us to carry out preventative debris clearance from our structures and reduced the risk of future flooding significantly.",
    name: "Andrew Simpson",
    role: "Structures Engineer, National Highways",
    logo: nationalHighwaysMark.url,
    cover: true,
  },
  {
    company: "Kent County Council",
    quote:
      "Snapshot Live provided us with complete visibility of the works site on a strategically important part of the network. The time-stamped imagery was used to verify site presence, monitor progression of the works, and ensure compliance with site set up and permit conditions. The camera is straightforward to install and modify as and where required.",
    name: "Ashley Bailey",
    role: "Senior Streetworks Coordinator, Kent County Council",
    logo: kentCrest.url,
    cover: true,
  },
  {
    company: "Crowdguard",
    quote:
      "For safety officers, assurance matters. Snapshot Live helps support that by providing a practical, non-intrusive way to evidence that agreed HVM measures appear to be in place at key stages of a live operation. It is about strengthening visibility, accountability and confidence in matchday environments and crowded spaces.",
    name: "Deborah Ainscough",
    role: "Founder of Crowdguard",
    logo: crowdguardBadge.url,
  },
];

const H2 = ({ children, className = "" }: { children: React.ReactNode; className?: string }) => (
  <h2 className={`font-archivo text-[28px] md:text-[40px] font-medium leading-[1.15] tracking-normal ${className}`}>{children}</h2>
);

const SectionHeader = ({ eyebrow, title, body, light = false, label = false, largeBody = false, bodyWidth = "max-w-[640px]" }: { eyebrow?: string; title: string; body?: React.ReactNode; light?: boolean; label?: boolean; largeBody?: boolean; bodyWidth?: string }) => (
  <div className={`flex flex-col items-center gap-3 text-center ${light ? "text-ns-white" : "text-ns-plum"}`}>
    {eyebrow && <p className={`text-[12px] font-medium leading-[19.1px] ${label ? "rounded bg-ns-white px-[10px] py-1" : ""} ${light ? "text-ns-white" : "text-ns-plum/50"}`}>{eyebrow}</p>}
    <H2>{title}</H2>
    {body && <div className={`w-full ${bodyWidth} ${largeBody ? "text-[18px] font-normal" : "text-[15px] font-medium"} leading-[1.5] ${light ? "text-ns-white" : "text-ns-plum/50"}`}>{body}</div>}
  </div>
);

const differentiators = [
  { icon: Aperture, title: "Less routine travel", body: "Verify condition remotely before dispatching people." },
  { icon: Zap, title: "Earlier intervention", body: "See emerging issues before they become incidents." },
  { icon: Shield, title: "Evidence-led assurance", body: "Create a consistent visual record between formal inspections." },
  { icon: Leaf, title: "Context for alerts", body: "See what is actually happening when a sensor or system flags risk." },
  { icon: FileText, title: "AI that prioritises", body: "Surface the minority of images that deserve human attention." },
  { icon: Zap, title: "Human effort where it matters", body: "Focus expertise on judgement, access, repair and intervention." },
];

const conditions = [
  { icon: Aperture, title: "Resilience", body: "Monitor flood-prone areas, water-level changes, and critical infrastructure without relying on fixed power or wired connectivity." },
  { icon: Zap, title: "Compliance", body: "Keep roadworks, lane closures, and temporary traffic management visible with scheduled images and on-demand checks." },
  { icon: Shield, title: "Safety", body: "Track site progress, monitor access points, safety; and maintain visibility across remote construction sites and facilities." },
];

export const productFeatures = [
  {
    title: "Periodic Images",
    solutionId: "periodic-image-schedules",
    body: "Schedule still-image capture at intervals that match your operational rhythm. Review progress, monitor conditions, and verify site status without relying on continuous livestreaming. Image capture schedules can be changed dynamically, set by week/weekend day or calendar.",
    detailBody: "View past images on your timeline, run reports or time lapses when you need to.",
    image: periodicImagesDashboard.url,
    imageAlt: "A laptop showing the Snapshot Live portal with a day of scheduled capture images and a scheduled images panel",
  },
  {
    title: "Instant Images",
    solutionId: "instant-on-demand-captures",
    body: "Request on-demand images when you need a closer look and cannot wait until the next scheduled image. Snapshot Live gives teams immediate visibility without the overhead of a continuous livestream.",
    image: instantImagesDeviceOverview.url,
    imageAlt: "A phone held in two hands showing the Snapshot Live device overview screen",
  },
  {
    title: "Privacy Guaranteed",
    solutionId: "privacy-blurring-protocols",
    body: "Apply privacy blurring to sensitive areas while preserving operational visibility. Control access, manage shared images, and maintain compliance across every deployment. Blurring includes people and number plates.",
    image: privacyGuaranteedStreet.url,
    imageAlt: "A street view with vehicle barriers detected and pedestrians masked for privacy",
  },
  {
    title: "Alerts & Notifications",
    solutionId: "ai-powered-alerts",
    body: "Receive notifications when activity changes, anomalies are detected, or site conditions shift. AI analysis helps teams respond faster without manual review of every image. Choose a query from our AI library or simply write your own and test it before committing it to your devices.",
    image: alertsNotificationsRoadworks.url,
    imageAlt: "A street-level view of collapsed red roadwork barriers and traffic cones along a grass verge beside a row of houses",
  },
];

const benefits = [
  { icon: Aperture, tone: "beige", title: "SPOT TRENDS — TIME LAPSE IMAGE LIBRARY FOR EACH LOCATION", body: "Review site history through a clean image library instead of searching through hours of video." },
  { icon: Zap, tone: "plum", title: "FOCUS WHERE IT MATTERS — AI ENABLED ALERTS HELP YOU PRIORITISE", body: "Receive notifications when activity changes, anomalies are detected, or site conditions shift." },
  { icon: Shield, tone: "gradient", title: "SEEING IS BELIEVING — INSTANT VISUAL TO CONFIRM RESPONSE", body: "Confirm response actions with clear still images instead of relying on verbal reports alone." },
];


// Collapsed answers are real Figma layers, hidden only in its initial accordion state.
const faqs = [
  { q: "Where can Snapshot Live cameras be installed?", a: "Snapshot Live cameras are designed for remote sites with limited power and connectivity. They can be pole-mounted in flood-prone areas, near streetworks, and across construction sites where fixed broadband is not available." },
  { q: "Do cameras need fixed power or wired broadband?", a: "No. Snapshot Live cameras are solar powered and cellular connected, so they can be deployed in locations where traditional infrastructure is unavailable or impractical." },
  { q: "How do AI alerts and notifications work?", a: "AI analysis runs on every scheduled and on-demand capture, detecting activity changes, anomalies, and exceptions. Teams receive notifications when something needs attention, helping them respond faster without reviewing every image manually." },
  { q: "Can sensitive areas be blurred or obscured?", a: "Yes. Privacy controls allow teams to apply blurring to sensitive areas while still maintaining operational visibility. Access can also be managed through secure cloud permissions and shared-image controls." },
  { q: "Can Snapshot Live integrate with existing workflows?", a: "Yes. The platform is designed to connect with existing systems, teams, and operational workflows, making it easier to align remote monitoring with the tools and processes your teams already use." },
];

export default function NewSiteHomeContent({ onContact }: { onContact: () => void }) {
  const [openFaq, setOpenFaq] = useState<number | null>(2);
  // Phones collapse the three AI benefit boxes to their headline; the plus icon opens the detail downwards.
  const [openBenefit, setOpenBenefit] = useState<string | null>(null);
  const [activeFeature, setActiveFeature] = useState(0);
  const [aiTab, setAiTab] = useState<"Resilience" | "Streetworks" | "Construction">("Resilience");
  const [testimonialIndex, setTestimonialIndex] = useState(0);
  const [testimonialsPaused, setTestimonialsPaused] = useState(false);
  const [testimonialHovered, setTestimonialHovered] = useState(false);
  const [testimonialFocused, setTestimonialFocused] = useState(false);
  const [testimonialVisible, setTestimonialVisible] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);
  const [isMobileHero, setIsMobileHero] = useState(
    () => typeof window !== "undefined" && window.matchMedia("(max-width: 767px)").matches
  );
  const testimonialSectionRef = useRef<HTMLElement>(null);
  const aiExamples = aiExamplesByTopic[aiTab];
  const featureSectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const preference = window.matchMedia("(prefers-reduced-motion: reduce)");
    const updatePreference = () => setReducedMotion(preference.matches);
    updatePreference();
    preference.addEventListener("change", updatePreference);
    const observer = new IntersectionObserver(([entry]) => {
      setTestimonialVisible(entry.isIntersecting);
    }, { threshold: 0.25 });
    if (testimonialSectionRef.current) observer.observe(testimonialSectionRef.current);
    return () => {
      preference.removeEventListener("change", updatePreference);
      observer.disconnect();
    };
  }, []);

  // Phones open on their own hero clip; larger screens keep the existing hero film.
  useEffect(() => {
    const phone = window.matchMedia("(max-width: 767px)");
    const updatePhone = () => setIsMobileHero(phone.matches);
    updatePhone();
    phone.addEventListener("change", updatePhone);
    return () => phone.removeEventListener("change", updatePhone);
  }, []);

  useEffect(() => {
    if (testimonialsPaused || testimonialHovered || testimonialFocused || !testimonialVisible || reducedMotion) return;
    const timer = window.setTimeout(() => {
      if (!document.hidden) setTestimonialIndex((i) => (i + 1) % testimonials.length);
    }, 3000);
    return () => window.clearTimeout(timer);
  }, [testimonialIndex, testimonialsPaused, testimonialHovered, testimonialFocused, testimonialVisible, reducedMotion]);

  useEffect(() => {
    let animationFrame = 0;

    const updateActiveFeature = () => {
      const section = featureSectionRef.current;
      if (!section) return;

      const bounds = section.getBoundingClientRect();
      const scrollableDistance = Math.max(section.offsetHeight - window.innerHeight, 1);
      const progress = Math.min(Math.max(-bounds.top / scrollableDistance, 0), 1);
      const nextFeature = Math.min(Math.round(progress * (productFeatures.length - 1)), productFeatures.length - 1);
      setActiveFeature((current) => (current === nextFeature ? current : nextFeature));
    };

    const onScroll = () => {
      cancelAnimationFrame(animationFrame);
      animationFrame = requestAnimationFrame(updateActiveFeature);
    };

    updateActiveFeature();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);

    return () => {
      cancelAnimationFrame(animationFrame);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, []);

  const currentFeature = productFeatures[activeFeature];

  return (
    <div className="ns-home">
      {/* Hero */}
      <section className="ns-home-hero relative h-[600px] md:h-[calc(100svh-100px)] md:min-h-[520px] md:max-h-[782px] overflow-hidden bg-ns-plum">
        <img src={isMobileHero ? heroMobilePoster.url : heroDesktopPoster.url} alt="Snapshot Live camera on site" className="absolute inset-0 h-full w-full object-cover" />
        <video
          key={isMobileHero ? "hero-mobile" : "hero-desktop"}
          src={isMobileHero ? heroMobileVideo.url : heroVideo.url}
          poster={isMobileHero ? heroMobilePoster.url : heroDesktopPoster.url}
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          aria-hidden="true"
          className="absolute inset-0 h-full w-full object-cover motion-reduce:hidden"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-ns-plum/80 via-ns-plum/20 to-transparent" />
        <div className="relative z-10 flex h-full w-full flex-col items-start justify-end gap-[29px] px-6 pb-14 text-left md:px-[72px] md:pb-20 text-ns-white">
          <h1 className="text-left font-archivo text-[32px] font-medium leading-[1.15] tracking-[-1.5px] text-ns-white md:text-[40px]">
            <span className="block mb-6 text-[12px] font-medium leading-[19.1px] tracking-normal text-ns-beige">Autonomous visual intelligence, anywhere.</span>
            <span className="block">A Smarter Way to See.</span>
            <span className="block">Lower cost, carbon and risk.</span>
          </h1>
          <div className="flex items-center gap-[11px]">
            <NsButton variant="lime" onClick={onContact}>Contact Us</NsButton>
            <NsButton variant="light" to="/case-studies" className="hidden md:inline-flex">Case Studies</NsButton>
            <NsButton variant="light" to="/case-studies" className="md:hidden">Case Studies</NsButton>
          </div>
        </div>
      </section>

      {/* Text panel (Figma: Desktop/Text Panel) */}
      <section className="ns-home-focus relative bg-ns-white px-6 py-20 md:px-16 md:py-[72px] xl:px-0">
        <div className="relative mx-auto flex w-full max-w-[924px] flex-col items-center justify-center gap-10 px-6 py-12 md:px-16 md:py-16">
          {[
            "left-0 top-0 border-l border-t",
            "right-0 top-0 border-r border-t",
            "left-0 bottom-0 border-l border-b",
            "right-0 bottom-0 border-r border-b",
          ].map((c) => (
            <span key={c} aria-hidden className={`absolute h-5 w-5 border-ns-plum ${c}`} />
          ))}
          <p className="text-center text-[15px] leading-[1.5] md:max-w-[33rem] md:text-[18px] xl:max-w-none">
            Image-based monitoring that delivers the insights you need, when you need them. No
            continuous video, no unnecessary site visits. Just actionable intelligence from anywhere.
          </p>
        </div>
      </section>

      {/* Video / product */}
      <section className="ns-home-video relative h-[420px] md:h-[567px] overflow-hidden bg-ns-plum">
        <img src={isMobileHero ? fostonBeckMobileAsset.url : fostonBeckAsset.url} alt="Snapshot Live SCC200 camera mounted on a wooden post beside a flooded watercourse at Foston Beck" className="absolute inset-0 h-full w-full object-cover" />
        {!isMobileHero && (
          <video
            src={fieldProvenVideo.url}
            poster={fostonBeckAsset.url}
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
            aria-hidden="true"
            className="absolute inset-0 h-full w-full object-cover motion-reduce:hidden"
          />
        )}
        <div className="ns-home-video-scrim absolute inset-0" />
        <h2 className="absolute bottom-10 right-6 md:bottom-[72px] md:right-[72px] max-w-[calc(100%-48px)] md:max-w-[707px] text-right font-archivo text-[24px] md:text-[46px] xl:text-[53px] font-medium leading-[1.15] tracking-normal text-ns-white">
          Designed to go
          <br />
          where others
          <br />
          can't
        </h2>
      </section>

      {/* Differentiators */}
      <section className="ns-home-differentiators bg-gradient-to-b from-ns-lime/70 to-ns-white px-6 md:px-[72px] py-20 md:py-[120px]">
        <SectionHeader
          eyebrow="Why Snapshot Live?"
          largeBody
          title="Eliminating the Visibility Gap"
          body="Snapshot Live combines autonomous hardware with AI to keep remote locations visible without the need for site visits or fixed infrastructure."
        />
        <Accordion type="single" collapsible defaultValue={differentiators[0].title} className="ns-visibility-accordion mt-8 flex flex-col gap-3 md:hidden">
          {differentiators.map(({ icon: Icon, title, body }) => (
            <AccordionItem key={title} value={title} className="rounded-lg border-0 bg-ns-white px-4">
              <AccordionTrigger className="gap-3 text-left text-ns-plum hover:no-underline">
                <span className="flex min-w-0 items-center gap-3"><Icon aria-hidden="true" className="h-5 w-5 shrink-0" /><span className="text-[16px] leading-[1.3]">{title}</span></span>
              </AccordionTrigger>
              <AccordionContent className="pl-8 text-[15px] leading-[1.5] text-ns-plum/70">{body}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
        <div className="mt-16 hidden auto-rows-fr gap-x-8 gap-y-16 md:grid md:grid-cols-2 lg:grid-cols-3">
          {differentiators.map(({ icon: Icon, title, body }) => (
            <div key={title} className="flex h-full min-w-0 flex-col items-start gap-4 rounded-lg bg-ns-white p-6">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center"><Icon className="h-5 w-5 text-ns-plum" /></div>
              <h3 className="font-archivo text-[18px] font-medium leading-[1.3]">{title}</h3>
              <p className="text-[15px] font-medium leading-[1.5] text-ns-plum/50">{body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Scroll-replacing product features; copy and order follow the four Figma feature frames. */}
      <section ref={featureSectionRef} className="ns-home-desktop-features relative hidden h-[200vh] bg-ns-white md:block" aria-label="Product features">
        <div className="sticky top-0 flex min-h-screen flex-col justify-center gap-8 overflow-hidden px-6 py-10 md:gap-16 md:px-[72px] md:py-[72px]">
          <SectionHeader
            eyebrow="Product & Features"
            label
            title="Simple & Powerful"
            body="Snapshot Live combines scheduled visibility, on-demand capture, AI-driven alerts, and privacy controls into a single easy to use remote-monitoring platform."
          />

          <div className="relative mx-auto w-full max-w-[1296px]">
            <article
              key={currentFeature.title}
              className="grid min-h-[390px] items-center gap-8 animate-in fade-in slide-in-from-bottom-12 duration-500 motion-reduce:animate-none md:min-h-[280px] md:grid-cols-[1fr_1fr] md:gap-16"
            >
              <div className="flex min-w-0 flex-col items-start gap-5">
                <h3 className="font-archivo text-[22px] font-medium leading-[1.3] tracking-normal md:text-[27px]">{currentFeature.title}</h3>
                <p className="max-w-[616px] text-[15px] font-medium leading-[1.5] text-ns-plum/50">{currentFeature.body}</p>
                <NsButton to={`/solution#${currentFeature.solutionId}`}>Learn More</NsButton>
              </div>
              <div className="order-first min-w-0 md:order-last">
                <img src={currentFeature.image} alt={currentFeature.imageAlt} className={`h-[190px] w-full rounded-lg ${(currentFeature as { contain?: boolean }).contain ? "object-contain" : "object-cover"} sm:h-[230px] md:h-[280px]`} />
              </div>
            </article>

            <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-2" aria-label={`Feature ${activeFeature + 1} of ${productFeatures.length}`}>
              {productFeatures.map((feature, index) => (
                <button
                  key={feature.title}
                  type="button"
                  aria-current={index === activeFeature ? "true" : undefined}
                  onClick={() => {
                    const section = featureSectionRef.current;
                    if (!section) return;
                    const scrollable = Math.max(section.offsetHeight - window.innerHeight, 1);
                    const top = section.getBoundingClientRect().top + window.scrollY;
                    window.scrollTo({ top: top + (index / (productFeatures.length - 1)) * scrollable + 2, behavior: reducedMotion ? "auto" : "smooth" });
                  }}
                  className={`group flex flex-col items-start gap-2 text-[13px] font-medium transition-colors ${index === activeFeature ? "text-ns-plum" : "text-ns-plum/45 hover:text-ns-plum/75"}`}
                >
                  <span aria-hidden="true" className={`h-1 rounded-full transition-[width,background-color] duration-300 motion-reduce:transition-none ${index === activeFeature ? "w-10 bg-ns-plum" : "w-4 bg-ns-plum/20 group-hover:bg-ns-plum/40"}`} />
                  {feature.title}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="ns-home-mobile-features bg-ns-white px-6 py-14 md:hidden" aria-label="Mobile product features">
        <SectionHeader eyebrow="Product & Features" title="Simple & Powerful" body="Snapshot Live combines scheduled visibility, on-demand capture, AI-driven alerts, and privacy controls into a single easy to use remote-monitoring platform." />
        <div className="ns-mobile-feature-grid mt-8 grid grid-cols-2 gap-4">
          {productFeatures.map((feature) => (
            <article key={feature.title} className="min-w-0 overflow-hidden rounded-lg bg-ns-beige/40">
              <img src={feature.image} alt={feature.imageAlt} loading="lazy" />
              <div className="flex flex-1 flex-col items-start gap-3 p-3">
                <h3 className="font-archivo font-medium">{feature.title}</h3>
                <NsButton className="mt-auto" to={`/solution#${feature.solutionId}`}>Learn More</NsButton>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* Collection (Figma: Desktop/Collection) */}
      <section className="ns-home-use-cases flex flex-col gap-16 bg-ns-beige px-6 md:px-[72px] py-20 md:py-[120px]">
        <SectionHeader
          eyebrow="Use Cases"
          bodyWidth="max-w-[645px]"
          title="Built for tough conditions"
          body={
            <>
              <p>
                Strengthen resilience, ensure compliance and improve safety with autonomous, image-based monitoring. Deploy
                almost anywhere, for a few days or several years, without the complexity of traditional monitoring systems.
              </p>
            </>
          }
        />
        <div className="grid auto-rows-fr grid-cols-1 gap-8 md:grid-cols-3">
          {conditions.map(({ icon: Icon, title, body }) => (
            <div key={title} className="flex h-full min-w-0 flex-col items-start gap-4 rounded-lg bg-ns-white p-6">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-ns-white">
                <Icon className="h-5 w-5" />
              </div>
              <h3 className="text-[18px] font-medium leading-[1.3]">{title}</h3>
              <p className="text-[15px] font-medium leading-[1.5] text-ns-plum/50">{body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Harnessing AI (Figma: desktop-harnessing-ai) — heading, tabs, analysis carousel and benefits on one plum glow. */}
      <section
        className="ns-home-ai relative flex flex-col gap-16 md:gap-[86px] overflow-hidden bg-ns-plum px-6 md:px-[72px] py-20 md:py-[120px] text-ns-white"
        style={{
          backgroundImage:
            "radial-gradient(ellipse 75% 95% at 30% 60%, hsl(var(--ns-plum)) 55%, transparent 100%), linear-gradient(90deg, transparent 55%, hsl(var(--ns-orange) / 0.85) 100%)",
        }}
      >
        <SectionHeader light eyebrow="Harnessing the Power of AI" title="AI on images delivers sharper insights, with simpler setup, at a fraction of the cost and complexity of video." />
        <div className="flex flex-col gap-[25px]">
          <div className="flex flex-wrap gap-2">
            {(["Resilience", "Streetworks", "Construction"] as const).map((tab) => (
              <NsButton key={tab} variant={aiTab === tab ? "lime" : "light"} onClick={() => setAiTab(tab)}>
                {tab}
              </NsButton>
            ))}
          </div>
          <div className="flex flex-col gap-[29px]">
            {isMobileHero ? (
              /* Phones show only the middle example — the highlighted alert — so portrait fits without a carousel. */
              <article className="ns-ai-card-featured flex min-w-0 flex-col gap-3 rounded-lg bg-ns-white p-4 text-ns-plum">
                <img src={aiExamples[2].image} alt={aiExamples[2].title} className="h-[215px] w-full rounded-lg object-cover" />
                <div className="flex flex-col gap-1">
                  <h3 className="font-archivo text-[15px] font-medium leading-[1.5]">{aiExamples[2].title}</h3>
                  <p className="text-[12px] font-medium leading-[19.1px] text-ns-plum/50">{aiExamples[2].body}</p>
                </div>
              </article>
            ) : (
              /* Tablets show three examples (alert in the middle); large screens show all five, locked in one row. */
              <div className="grid items-center gap-4 sm:grid-cols-[1fr_1.15fr_1fr] xl:grid-cols-[1fr_1fr_282px_1fr_1fr]">
                {aiExamples.map((example, slot) => {
                  const featured = slot === 2;
                  return (
                    <article key={slot} className={`${slot === 1 || slot === 3 ? "hidden xl:flex" : "flex"} min-w-0 flex-col gap-3 rounded-lg bg-ns-white p-4 text-ns-plum ${featured ? "ns-ai-card-featured" : ""}`}>
                      <img src={example.image} alt={example.title} className={`w-full rounded-lg object-cover ${featured ? "h-[215px]" : "h-40"}`} />
                      <div className="flex flex-col gap-1">
                        <h3 className="font-archivo text-[15px] font-medium leading-[1.5]">{example.title}</h3>
                        <p className="text-[12px] font-medium leading-[19.1px] text-ns-plum/50">{example.body}</p>
                      </div>
                    </article>
                  );
                })}
              </div>
            )}

          </div>
        </div>
        <div className="flex flex-col md:flex-row items-stretch gap-6">
          {benefits.map(({ icon: Icon, title, body }) => {
            const open = openBenefit === title;
            return (
              <div key={title} data-open={open} className="ns-benefit-card flex flex-1 flex-col items-start gap-4 rounded-lg bg-ns-white/40 p-6">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg">
                  <Icon className="h-5 w-5 text-ns-white" />
                </div>
                <div className="flex w-full items-start justify-between gap-4">
                  <p className="text-[15px] font-medium leading-[1.5] text-ns-white">{title}</p>
                  <button
                    type="button"
                    onClick={() => setOpenBenefit(open ? null : title)}
                    aria-expanded={open}
                    aria-label={`${open ? "Hide" : "Show"} details for ${title}`}
                    className="ns-benefit-toggle -mr-1 -mt-1 flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-ns-white/50 text-ns-white md:hidden"
                  >
                    <Plus className={`h-4 w-4 transition-transform duration-200 motion-reduce:transition-none ${open ? "rotate-45" : ""}`} />
                  </button>
                </div>
                <p className="ns-benefit-body text-[12px] font-medium leading-[19.1px] text-ns-white"><span>{body}</span></p>
              </div>
            );
          })}
        </div>
      </section>

      <section className="ns-home-operational-benefits hidden bg-ns-white" aria-label="Operational benefits">
        {benefits.map(({ icon: Icon, title, body, tone }) => (
          <article key={title} className={`flex flex-col items-start gap-4 rounded-lg p-6 ${tone === "beige" ? "bg-ns-beige text-ns-plum" : tone === "plum" ? "bg-ns-plum text-ns-white" : "bg-ns-orange text-ns-white"}`}>
            <div className="flex h-10 w-10 items-center justify-center"><Icon className="h-5 w-5" /></div>
            <p className="text-[15px] font-medium leading-[1.5]">{title}</p>
            <p className="text-[12px] font-medium leading-[19.1px] opacity-70">{body}</p>
          </article>
        ))}
      </section>


      <NewSiteProcessSection title="Operating Model" intro="Moving from reactive to proactive management with a human centric repeatable process." steps={operatingModelSteps} />

      {/* Trusted by */}
      <section className="ns-home-trusted bg-ns-white px-6 md:px-[72px] py-20 md:py-[120px]">
        <SectionHeader label eyebrow="Trusted By" title="Trusted by teams across industries" body="Snapshot Live supports teams with autonomous monitoring wherever visibility matters." bodyWidth="max-w-[590px]" />
        <ul className="mx-auto mt-[75px] grid w-full max-w-[887px] grid-cols-2 items-center gap-x-8 gap-y-12 sm:grid-cols-3 md:grid-cols-5 md:gap-y-16">
          {trustedLogos.map((logo) => (
            <li key={logo.name} className="flex h-[108px] items-center justify-center">
              <img src={logo.src} alt={logo.name} loading="lazy" style={{ width: logo.w, height: logo.h }} className="max-w-full object-contain" />
            </li>
          ))}
        </ul>
      </section>

      {/* Testimonial carousel (Figma: desktop-testimonials) */}
      <section
        ref={testimonialSectionRef}
        aria-label="Customer testimonials"
        onMouseEnter={() => setTestimonialHovered(true)}
        onMouseLeave={() => setTestimonialHovered(false)}
        onFocusCapture={() => setTestimonialFocused(true)}
        onBlurCapture={(event) => {
          if (!event.currentTarget.contains(event.relatedTarget)) setTestimonialFocused(false);
        }}
        className="ns-home-testimonials flex flex-col items-center gap-16 bg-ns-beige px-6 md:px-[72px] pt-24 md:pt-[150px] pb-16 md:pb-[100px]"
      >
        <div className="flex w-full items-center justify-between gap-4">
          <Button
            variant="ghost"
            type="button"
            aria-label="Previous testimonial"
            onClick={() => setTestimonialIndex((i) => (i - 1 + testimonials.length) % testimonials.length)}
            className="hidden h-8 w-8 shrink-0 items-center justify-center rounded-full text-ns-plum hover:bg-ns-plum/5 md:flex"
          >
            <ChevronLeft aria-hidden className="h-5 w-5" />
          </Button>
          <figure aria-live={testimonialsPaused || testimonialFocused || reducedMotion ? "polite" : "off"} className="w-full max-w-[724px] rounded-lg border border-ns-beige bg-ns-white p-6 md:p-8">
            <div className="flex flex-col gap-5">
              <blockquote className="min-h-[81px] font-normal text-[18px] leading-[1.5] text-ns-plum/50">
                "{testimonials[testimonialIndex].quote}"
              </blockquote>
              <div className="h-px w-full bg-ns-plum/20" />
              <figcaption className="flex items-center gap-3">
                <img src={testimonials[testimonialIndex].logo} alt={testimonials[testimonialIndex].company} className={`h-10 w-10 shrink-0 rounded-full ${testimonials[testimonialIndex].cover ? "object-cover" : "bg-ns-beige object-contain p-1"}`} />
                <div className="flex flex-col gap-0.5">
                  <span className="text-[15px] font-medium leading-[1.5]">{testimonials[testimonialIndex].name}</span>
                  <span className="text-[12px] font-medium leading-[19.1px] text-ns-plum/50">{testimonials[testimonialIndex].role}</span>
                </div>
              </figcaption>
            </div>
          </figure>
          <Button
            variant="ghost"
            type="button"
            aria-label="Next testimonial"
            onClick={() => setTestimonialIndex((i) => (i + 1) % testimonials.length)}
            className="hidden h-8 w-8 shrink-0 items-center justify-center rounded-full text-ns-plum hover:bg-ns-plum/5 md:flex"
          >
            <ChevronRight aria-hidden className="h-5 w-5" />
          </Button>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" aria-label="Previous testimonial" onClick={() => setTestimonialIndex((i) => (i - 1 + testimonials.length) % testimonials.length)} className="mr-auto h-11 w-11 text-ns-plum md:hidden"><ChevronLeft className="h-5 w-5" /></Button>
          {testimonials.map((t, i) => (
            <Button
              variant="ghost"
              key={t.company}
              type="button"
              aria-label={`Show the ${t.company} testimonial`}
              aria-current={i === testimonialIndex}
              onClick={() => setTestimonialIndex(i)}
              className={`h-2 w-2 rounded-full p-0 transition-colors hover:bg-ns-plum/60 ${i === testimonialIndex ? "bg-ns-plum" : "bg-ns-plum/30"}`}
            />
          ))}
          {!reducedMotion && (
            <Button variant="ghost" size="icon" type="button" aria-label={testimonialsPaused ? "Resume testimonials" : "Pause testimonials"} title={testimonialsPaused ? "Resume testimonials" : "Pause testimonials"} onClick={() => setTestimonialsPaused((paused) => !paused)} className="ml-2 h-8 w-8 rounded-full text-ns-plum hover:bg-ns-plum/5">
              {testimonialsPaused ? <Play aria-hidden className="h-4 w-4" /> : <Pause aria-hidden className="h-4 w-4" />}
            </Button>
          )}
          <Button variant="ghost" size="icon" aria-label="Next testimonial" onClick={() => setTestimonialIndex((i) => (i + 1) % testimonials.length)} className="ml-auto h-11 w-11 text-ns-plum md:hidden"><ChevronRight className="h-5 w-5" /></Button>
        </div>
      </section>

      {/* FAQ (Figma: Desktop/FAQ) */}
      <section className="ns-home-faq flex flex-col gap-12 bg-ns-white px-6 md:px-[80px] py-20 md:py-24">
        <SectionHeader
          eyebrow="FAQ"
          title="Frequently Asked Questions"
          body="Common questions about deployment, privacy, alerts, and how Snapshot Live works across remote sites."
        />
        <div className="flex flex-col md:flex-row items-start gap-10 md:gap-8">
          <div className="order-2 md:order-1 flex w-full md:w-[320px] shrink-0 flex-col items-start gap-4">
            <h3 className="text-[18px] font-medium leading-[1.3]">Need a faster answer?</h3>
            <p className="max-w-[269px] text-[12px] font-medium leading-[19.1px] text-ns-plum/50">
              Our FAQ covers the basics of hardware, connectivity, privacy, and AI alerts. If you still have questions, our
              team can help with pricing, rollout planning, and sector-specific use cases.
            </p>
            <NsButton onClick={onContact}>Contact Us</NsButton>
          </div>
          <div className="order-1 md:order-2 flex w-full flex-1 flex-col gap-3">
            {faqs.map((f, i) => (
              <div key={f.q} className="flex flex-col gap-3">
                <div className="h-px w-full bg-ns-plum/20" />
                <Button variant="ghost" aria-expanded={openFaq === i} aria-controls={`ns-faq-${i}`} className="h-auto w-full justify-between gap-4 whitespace-normal rounded-none p-0 text-left text-[18px] font-normal text-ns-plum hover:bg-transparent hover:text-ns-plum" onClick={() => setOpenFaq(openFaq === i ? null : i)}>
                  <span className="flex-1 leading-[1.5]">{f.q}</span>
                  <span className="font-medium leading-[1.3]">{openFaq === i ? "–" : "+"}</span>
                </Button>
                {openFaq === i && <p id={`ns-faq-${i}`} className="text-[12px] font-medium leading-[19.1px] text-ns-plum/50">{f.a}</p>}
              </div>
            ))}
            <div className="h-px w-full bg-ns-plum/20" />
          </div>
        </div>
      </section>

      {/* Linked panel (Figma: Desktop/Linked Panel) */}
      <section className="ns-home-closing flex flex-col items-center gap-8 overflow-hidden bg-gradient-to-r from-ns-orange via-ns-orange/80 to-ns-lime px-6 md:px-[72px] py-20 md:py-[120px] text-center">
        <div className="flex w-full max-w-[860px] flex-col items-center gap-4 text-ns-white">
          <H2 className="min-[880px]:whitespace-nowrap">Secure Your Operational Visibility Today</H2>
          <p className="max-w-[536px] text-[15px] font-medium leading-[1.5]">
            Speak with our team to discover how autonomous, image-based monitoring can improve visibility, reduce costs and help you make smarter, faster decisions.
          </p>
        </div>
        <div className="flex gap-4">
          <NsButton variant="lime" onClick={onContact}>Contact Us</NsButton>
          <NsButton variant="light" onClick={onContact}>Request Brochure</NsButton>
        </div>
      </section>
    </div>
  );
}
